// biome-ignore format: historic
// biome-ignore format: historic
// biome-ignore format: historic
import { LockableTextField } from '@components/Card2/LockableTextField';
import { LockButton } from '@components/Card2/LockableTextField/LockButton';
import Checkbox from '@components/Checkbox/Checkbox';
import PhoneNumberInput, {
  type OnChangeDetails,
  usePhoneNumberInput,
} from '@components/PhoneNumberInput/PhoneNumberInput';
import { useCardPopoverInputFocus } from '@hooks/card';
import { getSuggestionsByFieldType } from '@modules/card-helpers';
import { Box, Stack, TextField } from '@ui-lib';
import { type CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { useState } from 'react';

import SuggestionsSection, { useSuggestionsSection } from '../SuggestionsSection/SuggestionsSection';

interface PhoneNumberFieldsProps {
  value: string;
  valueLocked: boolean;
  valueLockEnabled?: boolean;
  label: string;
  labelLocked: boolean;
  labelLockEnabled?: boolean;
  labelLockedByTemplate?: boolean;
  phoneExtension: string;
  phoneCountryCode: CountryCode;
  setLabel: (value: string) => void;
  setValue: (value: string) => void;
  setValueLocked: (value: boolean) => void;
  setLabelLocked: (value: boolean) => void;
  setPhoneCountryCode: (value: CountryCode) => void;
  setPhoneExtension: (value: string) => void;
}

function PhoneNumberFields({
  value,
  valueLocked,
  valueLockEnabled,
  label,
  labelLocked,
  labelLockEnabled,
  labelLockedByTemplate,
  phoneCountryCode,
  phoneExtension,
  setValue,
  setLabel,
  setValueLocked,
  setLabelLocked,
  setPhoneCountryCode,
  setPhoneExtension,
}: PhoneNumberFieldsProps): React.ReactElement {
  const [valueHasBeenEdited, setValueHasBeenEdited] = useState(false);
  const phoneNumberInput = usePhoneNumberInput(value, phoneCountryCode, valueHasBeenEdited);

  const [isFormattedAsInternationalNumber, setIsFormattedAsInternationalNumber] = useState(() =>
    value.startsWith('+'),
  );

  const options = getSuggestionsByFieldType('phone')();

  const [selectedOption, setSelectedOption] = useSuggestionsSection(options, label);

  const { inputRef } = useCardPopoverInputFocus();

  function handleIntlPhoneNumberChange(details: OnChangeDetails) {
    setValueHasBeenEdited(true);
    phoneNumberInput.setPhoneNumber(details.number);
    phoneNumberInput.setCountryCode(details.countryCode);
    setValue(details.preservedNumber);
    setPhoneCountryCode(details.countryCode);
  }

  function handleLocalisedPhoneNumberChange(number: string) {
    phoneNumberInput.setPhoneNumber(number);
    setValue(number);
  }

  function handlePhoneNumberFormatChange(isInternational: boolean) {
    if (!isInternational) {
      handleLocalisedPhoneNumberChange(phoneNumberInput.phoneNumber);
    } else {
      setValueHasBeenEdited(true);
      const countryCallingCode = getCountryCallingCode(phoneNumberInput.countryCode || 'US');
      setValue(`+${countryCallingCode}${phoneNumberInput.phoneNumber}`);
      setPhoneCountryCode(phoneNumberInput.countryCode || 'US');
    }

    setIsFormattedAsInternationalNumber(isInternational);
  }

  function handleExtensionChange(extension: string) {
    setPhoneExtension(extension);
  }

  function handleSelectSuggestion(suggestion: string) {
    const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

    setSelectedOption(newSelectedOption);
    setLabel(newSelectedOption);
  }

  return (
    <Stack gap="large">
      <Stack>
        <Box display="flex" gap="xsmall" alignItems="center">
          {isFormattedAsInternationalNumber && (
            <PhoneNumberInput
              phoneNumber={phoneNumberInput.phoneNumber}
              countryCode={phoneNumberInput.countryCode}
              onChange={handleIntlPhoneNumberChange}
              ref={inputRef}
              disabled={valueLocked}
              required
            />
          )}

          {!isFormattedAsInternationalNumber && (
            <Box width="full">
              <TextField
                type="tel"
                autoComplete="tel-national"
                value={phoneNumberInput.phoneNumber}
                disabled={valueLocked}
                onChange={(event) => handleLocalisedPhoneNumberChange(event.currentTarget.value)}
                required
              />
            </Box>
          )}

          <Box style={{ maxWidth: '60px' }}>
            <TextField
              value={phoneExtension || ''}
              onChange={(event) => handleExtensionChange(event.currentTarget.value)}
              label="Ext."
              disabled={valueLocked}
            />
          </Box>

          {valueLockEnabled && (
            <LockButton locked={valueLocked} toggleLocked={() => setValueLocked(!valueLocked)} />
          )}
        </Box>
        <Checkbox
          onChange={handlePhoneNumberFormatChange}
          checked={isFormattedAsInternationalNumber}
          label="Use international number"
          size="small"
          type="subtle"
          disabled={valueLocked}
        />
      </Stack>

      <Stack gap="small">
        <LockableTextField
          value={label}
          onChange={(event) => setLabel(event.currentTarget.value)}
          label="Label (Optional)"
          locked={labelLocked}
          toggleLocked={labelLockEnabled ? () => setLabelLocked(!labelLocked) : undefined}
          explanation={labelLockedByTemplate ? 'Locked by template' : undefined}
        />
        {!labelLocked && options?.length > 0 && (
          <SuggestionsSection
            options={options}
            onSelect={handleSelectSuggestion}
            selectedOption={selectedOption}
            subject="label"
          />
        )}
      </Stack>
    </Stack>
  );
}

export default PhoneNumberFields;
